<template>
  <div class="p-6">
    <h2 class="text-xl font-bold mb-6" :class="isDark ? 'text-white' : 'text-gray-800'">时间转换</h2>

    <div class="space-y-6">
      <div class="grid md:grid-cols-2 gap-4">
        <div class="p-4 rounded-lg border" :class="isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'">
          <label class="block text-sm font-medium mb-3" :class="isDark ? 'text-gray-300' : 'text-gray-700'">当前时间戳</label>
          <div class="flex items-center gap-2">
            <span class="flex-1 font-mono text-lg" :class="isDark ? 'text-green-400' : 'text-green-600'">{{ currentTimestamp }}</span>
            <button
              @click="copyTimestamp"
              class="px-3 py-1 text-xs rounded transition-all"
              :class="isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
            >{{ copiedTs ? '已复制' : '复制' }}</button>
          </div>
          <button
            @click="refreshTimestamp"
            class="mt-3 text-sm underline"
            :class="isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'"
          >刷新</button>
        </div>

        <div class="p-4 rounded-lg border" :class="isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'">
          <label class="block text-sm font-medium mb-3" :class="isDark ? 'text-gray-300' : 'text-gray-700'">当前日期时间</label>
          <div class="font-mono text-lg" :class="isDark ? 'text-green-400' : 'text-green-600'">{{ currentDate }}</div>
        </div>
      </div>

      <div class="p-4 rounded-lg border" :class="isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'">
        <div class="font-medium mb-4" :class="isDark ? 'text-white' : 'text-gray-800'">时间戳 → 日期</div>
        <div class="space-y-3">
          <div>
            <label class="block text-sm mb-1" :class="isDark ? 'text-gray-400' : 'text-gray-500'">输入时间戳（秒或毫秒）</label>
            <input
              v-model="tsInput"
              type="text"
              placeholder="例如 1700000000 或 1700000000000"
              class="w-full px-4 py-2 rounded-lg border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500"
              :class="isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'"
            />
          </div>
          <div class="flex gap-2">
            <button
              @click="tsToDate"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              :class="isDark ? 'bg-green-500 text-white hover:bg-green-400' : 'bg-green-600 text-white hover:bg-green-500'"
            >转换</button>
          </div>
          <div v-if="tsResult" class="mt-3 p-3 rounded-lg" :class="isDark ? 'bg-gray-700' : 'bg-gray-100'">
            <div class="text-sm mb-1" :class="isDark ? 'text-gray-400' : 'text-gray-500'">转换结果</div>
            <div class="font-mono" :class="isDark ? 'text-green-400' : 'text-green-600'">{{ tsResult }}</div>
          </div>
        </div>
      </div>

      <div class="p-4 rounded-lg border" :class="isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'">
        <div class="font-medium mb-4" :class="isDark ? 'text-white' : 'text-gray-800'">日期 → 时间戳</div>
        <div class="space-y-3">
          <div>
            <label class="block text-sm mb-1" :class="isDark ? 'text-gray-400' : 'text-gray-500'">选择日期时间</label>
            <div class="flex gap-2">
              <input
                v-model="dateInput"
                type="datetime-local"
                step="1"
                class="flex-1 px-4 py-2 rounded-lg border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500"
                :class="isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'"
              />
              <input
                v-model="msInput"
                type="number"
                min="0"
                max="999"
                placeholder="000"
                title="毫秒（0-999）"
                class="w-20 px-3 py-2 rounded-lg border text-sm font-mono text-center focus:outline-none focus:ring-2 focus:ring-green-500"
                :class="isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'"
              />
            </div>
            <div class="text-xs mt-1" :class="isDark ? 'text-gray-500' : 'text-gray-400'">右侧输入毫秒（0-999，留空为 000）</div>
          </div>
          <div class="flex gap-2">
            <button
              @click="dateToTs"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              :class="isDark ? 'bg-green-500 text-white hover:bg-green-400' : 'bg-green-600 text-white hover:bg-green-500'"
            >转换</button>
          </div>
          <div v-if="dateResult" class="mt-3 p-3 rounded-lg" :class="isDark ? 'bg-gray-700' : 'bg-gray-100'">
            <div class="text-sm mb-1" :class="isDark ? 'text-gray-400' : 'text-gray-500'">时间戳</div>
            <div class="font-mono flex items-center gap-2" :class="isDark ? 'text-green-400' : 'text-green-600'">
              <span>秒：{{ dateResult.seconds }}</span>
              <button
                @click="copyText(String(dateResult.seconds))"
                class="px-2 py-0.5 text-xs rounded transition-all"
                :class="isDark ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'"
              >复制</button>
            </div>
            <div class="font-mono flex items-center gap-2 mt-1" :class="isDark ? 'text-green-400' : 'text-green-600'">
              <span>毫秒：{{ dateResult.milliseconds }}</span>
              <button
                @click="copyText(String(dateResult.milliseconds))"
                class="px-2 py-0.5 text-xs rounded transition-all"
                :class="isDark ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'"
              >复制</button>
            </div>
          </div>
        </div>
      </div>

      <div class="p-4 rounded-lg border" :class="isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'">
        <div class="font-medium mb-3" :class="isDark ? 'text-white' : 'text-gray-800'">常用时间戳</div>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div v-for="item in quickTimestamps" :key="item.label"
               class="p-3 rounded-lg cursor-pointer transition-all text-center"
               :class="isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'"
               @click="tsInput = String(item.ts); tsToDate()">
            <div class="text-xs mb-1" :class="isDark ? 'text-gray-400' : 'text-gray-500'">{{ item.label }}</div>
            <div class="font-mono text-sm" :class="isDark ? 'text-green-400' : 'text-green-600'">{{ item.ts }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useTheme } from '../../composables/useTheme'

export default {
  name: 'TimeConverter',
  setup() {
    const { isDark } = useTheme()
    const currentTimestamp = ref('')
    const currentDate = ref('')
    const tsInput = ref('')
    const tsResult = ref('')
    const dateInput = ref('')
    const msInput = ref('')
    // 限制毫秒输入在 0-999
    watch(msInput, (v) => {
      if (v === '') return
      const n = Number(v)
      if (Number.isNaN(n)) { msInput.value = ''; return }
      if (n > 999) msInput.value = '999'
      else if (n < 0) msInput.value = '0'
    })
    const dateResult = ref(null)
    const copiedTs = ref(false)
    const copiedText = ref('')

    const refreshTimestamp = () => {
      const now = new Date()
      currentTimestamp.value = String(Math.floor(now.getTime() / 1000))
      currentDate.value = formatDate(now)
    }

    const formatDate = (date) => {
      const pad = (n) => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    }

    const tsToDate = () => {
      const ts = Number(tsInput.value.trim())
      if (!ts) { tsResult.value = '请输入有效时间戳'; return }
      const ms = String(tsInput.value.trim()).length <= 10 ? ts * 1000 : ts
      const date = new Date(ms)
      if (isNaN(date.getTime())) { tsResult.value = '无效时间戳'; return }
      tsResult.value = `${formatDate(date)} (${date.toLocaleString()})`
    }

    const dateToTs = () => {
      if (!dateInput.value) { dateResult.value = null; return }
      const date = new Date(dateInput.value)
      if (isNaN(date.getTime())) { dateResult.value = null; return }
      const ms = Number(msInput.value)
      date.setMilliseconds(Number.isNaN(ms) ? 0 : Math.max(0, Math.min(999, ms)))
      dateResult.value = {
        seconds: Math.floor(date.getTime() / 1000),
        milliseconds: date.getTime()
      }
    }

    const quickTimestamps = computed(() => {
      const now = new Date()
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
      const weekStart = new Date(todayStart)
      weekStart.setDate(weekStart.getDate() - weekStart.getDay())
      return [
        { label: '现在', ts: Math.floor(Date.now() / 1000) },
        { label: '今日 00:00:00', ts: Math.floor(todayStart.getTime() / 1000) },
        { label: '今日 23:59:59', ts: Math.floor(todayEnd.getTime() / 1000) },
        { label: '本周一', ts: Math.floor(weekStart.getTime() / 1000) }
      ]
    })

    const copyTimestamp = async () => {
      await navigator.clipboard.writeText(currentTimestamp.value)
      copiedTs.value = true
      setTimeout(() => { copiedTs.value = false }, 1500)
    }

    const copyText = async (text) => {
      await navigator.clipboard.writeText(text)
      copiedText.value = text
      setTimeout(() => { copiedText.value = '' }, 1500)
    }

    refreshTimestamp()
    setInterval(refreshTimestamp, 1000)

    return {
      isDark, currentTimestamp, currentDate,
      tsInput, tsResult, dateInput, msInput, dateResult,
      copiedTs, copiedText, quickTimestamps,
      refreshTimestamp, tsToDate, dateToTs, copyTimestamp, copyText
    }
  }
}
</script>
